import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3,
  Link2, Image as ImageIcon, Undo2, Redo2, Strikethrough,
} from "lucide-react";

const ToolbarBtn = ({ onClick, active, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`p-2 hover:bg-zinc-200 border-r border-zinc-300 ${active ? "bg-zinc-200" : ""}`}
  >
    {children}
  </button>
);

export default function RichEditor({ value, onChange, onRequestImage }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Image,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-article max-w-none focus:outline-none min-h-[300px] p-4 bg-white",
        "data-testid": "rich-editor-content",
      },
    },
  });

  // sync external value changes (e.g., loading saved article)
  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const url = window.prompt("URL do link", "https://");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const insertImage = async () => {
    if (onRequestImage) {
      const url = await onRequestImage();
      if (url) editor.chain().focus().setImage({ src: url }).run();
    } else {
      const url = window.prompt("URL da imagem");
      if (url) editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="border border-zinc-300" data-testid="rich-editor">
      <div className="flex flex-wrap border-b border-zinc-300 bg-zinc-50">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrito" testid="rt-bold"><Bold size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Itálico" testid="rt-italic"><Italic size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado" testid="rt-strike"><Strikethrough size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Subtítulo H2" testid="rt-h2"><Heading2 size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subtítulo H3" testid="rt-h3"><Heading3 size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista" testid="rt-ul"><List size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada" testid="rt-ol"><ListOrdered size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citação" testid="rt-quote"><Quote size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={promptLink} active={editor.isActive("link")} title="Link" testid="rt-link"><Link2 size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={insertImage} title="Inserir imagem" testid="rt-image"><ImageIcon size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Desfazer" testid="rt-undo"><Undo2 size={14}/></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Refazer" testid="rt-redo"><Redo2 size={14}/></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
